import React, { useState, Fragment } from 'react';
import classNames from 'classnames';

import { handleEnter } from 'common/dom_helpers/keyPressHelpers';
import { I18n } from 'common/i18n';
import { Filters, getEnabledFilters } from 'common/components/AssetBrowser/lib/helpers/enabled_filters';
import { Tab } from '../../lib/constants';
import { ForgeStack } from '@tylertech/forge-react';

import ClearFilters from './clear_filters';
import CategoryFilter from './category_filter';
import CustomFacetFilters from './custom_facet_filters';
import OwnedByFilter from './owned_by_filter';
import TagFilter from './tag_filter';
import RequesterFilter from './requester';
import { FilterLookup } from './config/filter-config';
import { CheckboxFilter } from './checkbox/checkbox-filter';
import { SelectDropdownFilter } from './select-dropdown/select-dropdown-filter';
import { FilterType } from './config/types';

// Render the appropriate component based on filter type
// TODO: So far used for Checkbox and Select Dropdown filters. Ticket for Autocomplete: EN-86190
// TODO EN-83164: This won't need to be exported if mobile filters component is removed.
export const getFilterComponent = (filterName: Filters): JSX.Element | null => {
  // Lookup filter config
  const filterConfig = FilterLookup[filterName];

  if (!filterConfig) {
    console.warn(`No filter configuration found for ${filterName}. It needs to be added to FilterLookup.`);
    return null;
  }

  // Render component based on filter type
  switch (filterConfig.filterType) {
    case FilterType.Checkbox:
      return <CheckboxFilter filterConfig={filterConfig} />;
    case FilterType.SelectDropdown:
      return <SelectDropdownFilter filterConfig={filterConfig} />;
  }
};

export const CatalogFilters = ({
  filterOrder,
  activeTab,
  showAwaitingApprovalFilter,
}: {
  filterOrder: Filters[];
  activeTab: Tab;
  showAwaitingApprovalFilter: boolean;
}) => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(true);

  const handleFilterContentToggleClick = () => {
    setIsFilterDrawerOpen((prev) => !prev);
  };

  const filterContentClass = classNames('filter-content', { hidden: !isFilterDrawerOpen });

  const openFiltersButton = !isFilterDrawerOpen ? (
    <button
      className="open-filters filter-content-toggle"
      onClick={handleFilterContentToggleClick}
      onKeyDown={handleEnter(handleFilterContentToggleClick, true)}
      aria-label={I18n.t('shared.asset_browser.filters.desktop.expand')}
    >
      <span
        aria-hidden="true"
        className="socrata-icon-arrow-left"
        title={I18n.t('shared.asset_browser.filters.desktop.expand')}
      />
    </button>
  ) : null;

  const closeFiltersButton = isFilterDrawerOpen ? (
    <button
      className="close-filters filter-content-toggle"
      onClick={handleFilterContentToggleClick}
      onKeyDown={handleEnter(handleFilterContentToggleClick, true)}
    >
      {I18n.t('shared.asset_browser.filters.desktop.hide')}
      <span
        aria-hidden="true"
        className="socrata-icon-arrow-right"
        title={I18n.t('shared.asset_browser.filters.desktop.contract')}
      />
    </button>
  ) : null;

  const filterHeader = (
    <div className="catalog-filters-header">
      <ClearFilters />
      {closeFiltersButton}
    </div>
  );

  const enabledFilters = getEnabledFilters(activeTab, showAwaitingApprovalFilter);

  return (
    <div className="catalog-filters">
      {openFiltersButton}

      <div className={filterContentClass}>
        {filterHeader}
        <form>
          <div className="filter-switches">
            <ForgeStack gap={0}>
              {filterOrder.map((filterName) => {
                return (
                  enabledFilters[filterName] && (
                    <Fragment key={filterName}>{getFilterComponent(filterName)}</Fragment>
                  )
                );
              })}
            </ForgeStack>
          </div>
          {/* TODO EN-86190: Delete all filters below when autocomplete dropdown is converted to reusable component*/}
          {enabledFilters.ownedBy && <OwnedByFilter />}
          <div className="temp-extra-padding">
            {enabledFilters.visibility && getFilterComponent(Filters.Visibility)}
          </div>
          {enabledFilters.category && <CategoryFilter />}
          {enabledFilters.tag && <TagFilter />}
          {enabledFilters.customFacet && <CustomFacetFilters />}
          {enabledFilters.requester && <RequesterFilter />}
        </form>
      </div>
    </div>
  );
};
